import { lazy, Suspense, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminDashboardApi, adminTenantsApi, adminFoundationApi } from '../../services/api';
import { NexusCard, NexusBadge, NexusTable, NexusButton } from '../../components/nexus-ui';
import Sparkline from '../../components/charts/Sparkline';
import SetupWizard from '../../components/SetupWizard';
import { Users, MessageSquare, Brain, Database, Settings, Upload, Plus, Wand2, ArrowUpRight } from 'lucide-react';
import { LiveNumber } from '../../components/instrument/LiveNumber';
import { Eyebrow } from '../../components/instrument';

const ParticleNetwork = lazy(() => import('../../components/three/ParticleNetwork'));

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
};

function MetricCard({ title, value, icon: Icon, color = '#2E70AD', sparkData = [], suffix = '' }) {
  return (
    <motion.div variants={fadeUp} className="ig-elevated p-4 relative overflow-hidden group">
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
          <Icon size={15} style={{ color }} />
        </div>
        <ArrowUpRight size={12} className="text-nexus-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <Eyebrow>{title}</Eyebrow>
      <div className="ig-display text-[28px] mt-1 text-nexus-text-primary leading-[1.1]" style={{ letterSpacing: '-0.02em' }}>
        {suffix === '₹' ? <LiveNumber value={value || 0} prefix="₹" /> : <LiveNumber value={value || 0} />}
      </div>
      {sparkData.length > 0 && (
        <div className="mt-3 opacity-80">
          <Sparkline data={sparkData} color={color} width={160} height={28} />
        </div>
      )}
    </motion.div>
  );
}

const AdminDashboard = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [wizardAutoTriggered, setWizardAutoTriggered] = useState(false);

  const { data: stats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: () => adminDashboardApi.getStats().then(r => r.data),
  });

  const { data: tenants } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => adminTenantsApi.list().then(r => r.data),
  });

  const { data: kbs } = useQuery({
    queryKey: ['foundationKBs'],
    queryFn: () => adminFoundationApi.listKBs().then(r => r.data),
  });

  // Auto-show wizard when no tenants exist (first time only)
  useEffect(() => {
    if (tenants && tenants.length === 0 && !localStorage.getItem('capex-wizard-dismissed')) {
      setShowWizard(true);
      setWizardAutoTriggered(true);
    }
  }, [tenants]);

  const { data: sparklines } = useQuery({
    queryKey: ['adminSparklines'],
    queryFn: () => adminDashboardApi.getSparklines().then(r => r.data),
  });

  const metrics = [
    { title: 'Total Tenants',     value: stats?.total_tenants || 0,        icon: Users,         color: '#1E4E8C', sparkData: sparklines?.tenants || [] },
    { title: 'Conversations',     value: stats?.total_conversations || 0,  icon: MessageSquare, color: '#F26B1F', sparkData: sparklines?.conversations || [] },
    { title: 'Tokens Used',       value: stats?.total_tokens_used || 0,    icon: Brain,         color: '#92580A', sparkData: sparklines?.tokens || [] },
    { title: 'Foundation Chunks', value: stats?.foundation_chunks || 0,    icon: Database,      color: '#2E70AD', sparkData: sparklines?.foundation_chunks || [] },
  ];

  const tenantColumns = [
    { key: 'name', header: 'Tenant', render: (v) => <span className="font-medium text-nexus-text-primary">{v}</span> },
    { key: 'industry', header: 'Industry' },
    {
      key: 'plan_tier', header: 'Plan',
      render: (v) => <NexusBadge variant={v === 'enterprise' ? 'premium' : v === 'professional' ? 'success' : 'info'}>{v}</NexusBadge>,
    },
    {
      key: 'is_active', header: 'Status',
      render: (v) => <NexusBadge variant={v ? 'success' : 'error'} pulse={v}>{v ? 'Active' : 'Inactive'}</NexusBadge>,
    },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header with Particle Network — intentionally dark regardless of theme */}
      <div className="dark relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0D1321] to-[#0A0E1A] border border-white/5 p-8 min-h-[160px]">
        <Suspense fallback={null}>
          <ParticleNetwork />
        </Suspense>
        <div className="relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-3xl font-bold"
            style={{ color: '#F3F4F6' }}
          >
            Platform Overview
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-2 text-sm"
            style={{ color: '#A8B0C0' }}
          >
            Monitor your GPT-as-a-Service platform performance
          </motion.p>
        </div>
      </div>

      {/* Metrics Grid */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {metrics.map((m) => (
          <MetricCard key={m.title} {...m} />
        ))}
      </motion.div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tenants */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-nexus-text-primary">Recent Tenants</h3>
                <NexusBadge variant="info">{tenants?.length || 0} total</NexusBadge>
              </div>
              <NexusTable columns={tenantColumns} data={(tenants || []).slice(0, 5)} loading={!tenants} emptyMessage="No tenants yet" />
            </div>
          </NexusCard>
        </motion.div>

        {/* Foundation Knowledge */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-nexus-text-primary">Foundation Knowledge</h3>
              </div>
              {kbs?.length > 0 ? (
                <div className="space-y-3">
                  {kbs.slice(0, 4).map((kb, i) => (
                    <motion.div
                      key={kb.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + i * 0.08 }}
                      className="p-3 bg-nexus-bg-tertiary/50 rounded-lg flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-nexus-accent-mint/10 flex items-center justify-center">
                          <Database size={18} className="text-nexus-accent-mint" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-nexus-text-primary">{kb.name}</p>
                          <p className="text-xs text-nexus-text-tertiary">{kb.total_chunks || 0} chunks</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Database size={40} className="text-nexus-text-tertiary mx-auto mb-3" />
                  <p className="text-nexus-text-secondary text-sm">No foundation knowledge uploaded</p>
                </div>
              )}
            </div>
          </NexusCard>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
        <NexusCard variant="glass" hover={false}>
          <div className="p-6">
            <h3 className="font-heading text-lg font-semibold text-nexus-text-primary mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <button
                  onClick={() => { setWizardAutoTriggered(false); setShowWizard(true); }}
                  className="block w-full text-left p-4 bg-gradient-to-br from-nexus-accent-mint/10 to-nexus-accent-violet/10 rounded-lg border border-nexus-accent-mint/20 hover:border-nexus-accent-mint/40 transition-colors"
                  data-testid="open-setup-wizard"
                >
                  <Wand2 size={28} className="text-nexus-accent-mint mb-2" />
                  <p className="font-medium text-nexus-text-primary text-sm">Setup Wizard</p>
                  <p className="text-xs text-nexus-text-tertiary">Onboard tenant in minutes</p>
                </button>
              </motion.div>
              {[
                { href: '/admin/gpt-config', icon: Settings, color: '#1E4E8C', title: 'Configure GPT', desc: 'Edit system prompts & models' },
                { href: '/admin/knowledge', icon: Upload, color: '#F26B1F', title: 'Upload Knowledge', desc: 'Add foundation documents' },
                { href: '/admin/tenants', icon: Plus, color: '#92580A', title: 'Add Tenant', desc: 'Onboard new customer' },
              ].map(({ href, icon: Ic, color, title, desc }) => (
                <motion.div key={href} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link to={href} className="block p-4 bg-nexus-bg-tertiary/50 rounded-lg border border-white/5 hover:border-nexus-border-emphasized transition-colors">
                    <Ic size={28} style={{ color }} className="mb-2" />
                    <p className="font-medium text-nexus-text-primary text-sm">{title}</p>
                    <p className="text-xs text-nexus-text-tertiary">{desc}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </NexusCard>
      </motion.div>

      {/* Setup Wizard */}
      <SetupWizard
        isOpen={showWizard}
        onClose={() => {
          setShowWizard(false);
          if (wizardAutoTriggered) localStorage.setItem('capex-wizard-dismissed', 'true');
        }}
        autoTriggered={wizardAutoTriggered}
      />
    </div>
  );
};

export default AdminDashboard;
